<script setup>
// 視覺稿與動畫參考影片未進版控，需要時向設計端索取。
//
// 捲動採整屏分頁（scroll-snap），順序為：
//   0 進站畫面：只有風景（對應影片 0～6.5 秒）
//   1 首屏：導覽列由上而下落下、文案自左側緩慢進入、樹林與人物自右下角放大鋪開（影片 6.5 秒之後）
//   2 本週精選步道 → 3 更多互動玩法 → 4 頁尾
// 0 與 1 是「同一個畫面」：風景用 sticky 釘住不動，只有上面的元素在變，
// 所以捲動時風景不會被推走（下方兩個等高的 snap 區塊只負責提供兩個停留點）。
// 風景是四層素材疊出來的（底圖／山／樹／人物），四張圖必須維持原稿的相對位置，
// 統一放進一個等比例的 stage 內再各自變形。
import baseImg from '~/assets/img/hero-base.png'
import mountImg from '~/assets/img/hero-mountain.png'
import treeImg from '~/assets/img/hero-tree.png'
import peopleImg from '~/assets/img/hero-people.png'
import calendarIcon from '~/assets/img/icon-calendar.png'
import arrowIcon from '~/assets/img/icon-arrow.png'
import pinIcon from '~/assets/img/icon-pin.png'
import cloud1 from '~/assets/img/cloud-1.png'
import cloud2 from '~/assets/img/cloud-2.png'
import cloud3 from '~/assets/img/cloud-3.png'
import hillImg from '~/assets/img/hill.png'
import playCloud from '~/assets/img/play-cloud.png'
import playJump from '~/assets/img/play-jump.png'
import playBulb from '~/assets/img/play-bulb.png'
import playGift from '~/assets/img/play-gift.png'
import playCardBlue from '~/assets/img/play-card-blue.png'
import playCardPurple from '~/assets/img/play-card-purple.png'
import ridgeImg from '~/assets/img/ridge.png'
import caretIcon from '~/assets/img/icon-caret.png'
import shareHomeImg from '~/assets/img/share/home.png'

definePageMeta({ layout: false })

// 分享首頁時 LINE / Facebook 抓的卡片圖；og:image 只吃絕對網址，打包後的路徑要補上網域
const pageOrigin = useRequestURL().origin
const shareImage = /^https?:\/\//i.test(shareHomeImg) ? shareHomeImg : pageOrigin + shareHomeImg

useHead({
  title: useRuntimeConfig().public.siteName,
  meta: [
    { property: 'og:url', content: `${pageOrigin}/` },
    { property: 'og:image', content: shareImage },
    { property: 'og:image:width', content: '654' },
    { property: 'og:image:height', content: '341' },
    { name: 'twitter:card', content: 'summary_large_image' }
  ]
})

const api = useApi()
const { resolveFileUrl } = useFileUrl()

// 首屏文案與精選步道都由後端決定，SSR 期間就取回，避免進站先閃一輪佔位內容。
// 兩支介面都是公開的（不帶 token），介面掛掉時首屏退回設計稿原文案，精選步道則整段留白。
const { data: home } = await useAsyncData('home', async () => {
  const [hero, featured] = await Promise.all([
    api.get('/front/home/hero').catch(() => null),
    api.get('/front/home/featured-trails').catch(() => null)
  ])
  return { hero, featured }
})

const activity = computed(() => home.value?.hero?.activity || null)

// 視覺稿的日期是民國年（115.09.20），區間同一年時結束日只留月日
function rocParts(date) {
  if (!date) return null
  const [y, m, d] = String(date).slice(0, 10).split('-')
  if (!y || !m || !d) return null
  return { year: Number(y) - 1911, md: `${m}.${d}` }
}

// 首屏只會拿到進行中或最近一檔已結束的活動（5.14），不是進行中就是投票期已過，
// 文案改成回顧上一檔並把人帶去看排行榜。
// 系統連一檔已結束的活動都沒有時 activity 為 null，一律退回設計稿原文案（下面各處的 fallback）
const ended = computed(() => !!activity.value && !activity.value.ongoing)

const periodText = computed(() => {
  const from = rocParts(activity.value?.start_date)
  const to = rocParts(activity.value?.end_date)
  if (!from || !to) return '115.09.20 – 10.16'
  return `${from.year}.${from.md} – ${from.year === to.year ? to.md : `${to.year}.${to.md}`}`
})

const eyebrowText = computed(() => activity.value?.activity_name || '115年度全民登山步道推廣計畫')

const leadText = computed(() => {
  if (ended.value) {
    return '目前沒有進行中的步道票選活動，感謝您對全國步道推廣計劃的關注，敬請期待下一屆活動資訊。'
  }
  // 獎品全名太長會把首屏擠成三行，這一段照設計稿寫死，換獎品要一起改這裡
  return '從郊山到縱谷，全臺步道等你來認識。完成投票，還有機會抽中 iPhone Duo、Insta360 Luna Ultra 與登山好禮。'
})

// 活動已結束時第三顆膠囊（公布日期）換成連往排行榜的按鈕，前兩顆的參與資格照舊
const pills = computed(() => {
  const announce = rocParts(activity.value?.announce_date)
  const base = ['持 GOOGLE／LINE 帳號即可參與', '每人每日 4 票']
  if (ended.value) return base
  return [
    ...base,
    announce ? `票選結果 ${announce.year}.${announce.md} 公布` : '票選結果將於活動結束後公布'
  ]
})

// 步道照片沒設定封面時，沿用貼近視覺稿照片色調的漸層佔位（tone），四張卡各一色
const TONES = ['sunrise', 'aurora', 'fjord', 'moss']

const trails = computed(() =>
  (home.value?.featured?.trails || []).map((t, i) => ({
    id: t.id,
    name: t.trail_name,
    area: [t.city, t.district].filter(Boolean).join(''),
    zone: t.zone_name,
    level: t.difficulty || '—',
    length: t.distance === null || t.distance === undefined ? '—' : `${Number(t.distance)}km`,
    duration: t.duration || '—',
    tags: t.tags || [],
    votes: Number(t.votes || 0),
    cover: resolveFileUrl(t.cover),
    tone: TONES[i % TONES.length]
  }))
)

// bg 是已經把缺角造型、圓角與菱格紋理烘好的卡片底圖，直接拉滿卡片即可
const playCards = [
  {
    key: 'quiz',
    badge: '·山林問答挑戰·',
    title: '挑戰山林知識，你能答對幾題？',
    desc: '選擇北、中、東、南任一區域，隨機挑戰步道題目，邊答題邊認識臺灣山林！',
    action: '開始測驗',
    // TODO: 山林問答
    // to: '/challenge',
    to: '/',
    art: playBulb,
    bg: playCardBlue
  },
  {
    key: 'stamp',
    badge: '·主題形象蓋章·',
    title: '為每條步道選取最適合的主題，蓋下你心中的特色印象！',
    desc: '每日最多40次蓋章機會，依步道特色自由分配四大主題數位，為步道蓋上您心中的特色印象！還有機會獲得抽獎機會！',
    action: '立即蓋章',
    to: '/stamp',
    art: playGift,
    bg: playCardPurple
  }
]

// 登入彈窗開著的時候，方向鍵要留給彈窗，不能還在後面翻頁
const { isLoginOpen } = useLoginDialog()

const scrollerRef = ref(null)
const mx = ref(0)
const my = ref(0)
const pastIntro = ref(false)

useReveal(scrollerRef)

let raf = 0
let reduced = false
let scrollRaf = 0
// 捲動剛發生的這段時間內不吃 mousemove（原因見 onMove）
let quietUntil = 0

// 整屏分頁：滾輪交給原生吸附會變成「使用者捲一點、瀏覽器再把畫面拉回去」的拉扯，
// 一次滾輪就頓一下。改成攔下滾輪、自己算出下一個停留點、用原生平滑捲動一次到位，
// 動畫期間的滾輪一律吞掉，捲動就只剩一段乾淨的位移。
let paging = false
let lastWheelAt = 0
let holdUntil = 0
let releaseTimer = 0
let endTimer = 0
let pagerMq = null
let hasScrollEnd = false

// 手機與矮螢幕本來就退回一般捲動（見樣式的 media query），分頁器也要跟著關
function pagerOn() {
  return !reduced && !!pagerMq?.matches
}

// 停留點直接量元素位置，版面調整了也不必回來改；
// 頁尾是 align: end，位置會被夾在最底，剛好等於它該停的地方
function readStops() {
  const el = scrollerRef.value
  if (!el) return []
  const base = el.getBoundingClientRect().top - el.scrollTop
  const max = Math.max(0, el.scrollHeight - el.clientHeight)
  const tops = Array.from(el.querySelectorAll('[data-stop]'), (node) =>
    Math.min(Math.max(Math.round(node.getBoundingClientRect().top - base), 0), max)
  )
  return Array.from(new Set(tops)).sort((a, b) => a - b)
}

function nearestStop(stops, top) {
  let best = 0
  for (let i = 1; i < stops.length; i += 1) {
    if (Math.abs(stops[i] - top) < Math.abs(stops[best] - top)) best = i
  }
  return best
}

function pageBy(dir) {
  const el = scrollerRef.value
  if (!el) return
  const stops = readStops()
  const next = nearestStop(stops, el.scrollTop) + dir
  if (next < 0 || next >= stops.length) return
  if (Math.abs(stops[next] - el.scrollTop) < 2) return

  paging = true
  holdUntil = 0
  el.scrollTo({ top: stops[next], behavior: 'smooth' })
  clearTimeout(endTimer)
  // scrollend 還不是每個瀏覽器都有，壓一個保險時間，免得動畫沒收尾就把捲動鎖死
  endTimer = setTimeout(releasePager, 900)
  if (hasScrollEnd) el.addEventListener('scrollend', onScrollEnd, { once: true })
}

function onScrollEnd() {
  clearTimeout(endTimer)
  releasePager()
}

// 觸控板放開手之後慣性還會再送一陣滾輪事件，要等它安靜下來才解鎖，
// 否則一次滑動會連跳好幾屏；但也不能無限等，最多再撐半秒就放行
function releasePager() {
  clearTimeout(releaseTimer)
  const now = performance.now()
  if (!holdUntil) holdUntil = now + 500
  if (now < holdUntil && now - lastWheelAt < 90) {
    releaseTimer = setTimeout(releasePager, 90)
    return
  }
  holdUntil = 0
  paging = false
}

function onWheel(e) {
  // ctrl + 滾輪是縮放，不能攔
  if (!pagerOn() || e.ctrlKey) return
  e.preventDefault()
  lastWheelAt = performance.now()
  if (paging || Math.abs(e.deltaY) < 2) return
  pageBy(e.deltaY > 0 ? 1 : -1)
}

// 鍵盤也走同一套，免得方向鍵捲到兩屏中間卡住
const KEY_DIR = { ArrowDown: 1, PageDown: 1, ArrowUp: -1, PageUp: -1 }

function onKey(e) {
  if (isLoginOpen.value) return
  if (!pagerOn() || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return
  const el = e.target
  if (el?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el?.tagName || '')) return
  const dir = KEY_DIR[e.key]
  if (!dir) return
  e.preventDefault()
  if (!paging) pageBy(dir)
}

// 滑鼠視差：只更新 CSS 變數，實際位移交給 CSS 算，避免每幀重排
function onMove(e) {
  // 捲動時游標其實沒動、是內容在動，瀏覽器仍會補送 mousemove；
  // 那一刻重算四層視差只會跟捲動搶主執行緒，直接略過
  if (reduced || raf || performance.now() < quietUntil) return
  const rect = e.currentTarget.getBoundingClientRect()
  const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1
  const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1
  raf = requestAnimationFrame(() => {
    raf = 0
    mx.value = Math.max(-1, Math.min(1, nx))
    my.value = Math.max(-1, Math.min(1, ny))
  })
}

function resetMove() {
  mx.value = 0
  my.value = 0
}

// 捲離進站畫面才放導覽列下來、播首屏文案、把樹林與人物從右下角放大鋪開；
// 捲回去會自動還原成進站畫面的樣子
function syncPane() {
  const el = scrollerRef.value
  if (!el) return
  // 進場與退場用兩條界線做遲滯，停在臨界點附近時才不會反覆切換、整屏跟著抖
  const ratio = el.scrollTop / el.clientHeight
  if (!pastIntro.value && ratio > 0.35) pastIntro.value = true
  else if (pastIntro.value && ratio < 0.2) pastIntro.value = false
}

// 捲動事件密度很高，量測與切換併到下一個繪製幀做，捲動本身才不會被主執行緒卡住
function onScroll() {
  quietUntil = performance.now() + 120
  if (scrollRaf) return
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = 0
    syncPane()
  })
}

function toNextPane() {
  if (pagerOn()) {
    pageBy(1)
    return
  }
  const el = scrollerRef.value
  if (!el) return
  el.scrollTo({ top: el.clientHeight, behavior: 'smooth' })
}

onMounted(() => {
  reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  // 門檻要跟樣式裡關掉整屏分頁的兩個 media query 對齊
  pagerMq = window.matchMedia('(min-width: 901px) and (min-height: 741px)')
  hasScrollEnd = 'onscrollend' in window

  const el = scrollerRef.value
  el?.addEventListener('scroll', onScroll, { passive: true })
  // 要 preventDefault 攔下滾輪，所以不能是 passive
  el?.addEventListener('wheel', onWheel, { passive: false })
  window.addEventListener('keydown', onKey)
  syncPane()
})

onBeforeUnmount(() => {
  if (raf) cancelAnimationFrame(raf)
  if (scrollRaf) cancelAnimationFrame(scrollRaf)
  clearTimeout(endTimer)
  clearTimeout(releaseTimer)

  const el = scrollerRef.value
  el?.removeEventListener('scroll', onScroll)
  el?.removeEventListener('wheel', onWheel)
  el?.removeEventListener('scrollend', onScrollEnd)
  window.removeEventListener('keydown', onKey)
})
</script>

<template>
  <div ref="scrollerRef" class="scroller">
    <div class="head-fix" :class="{ 'is-on': pastIntro }">
      <SiteHeader active="home" />
    </div>

    <!-- 0 進站畫面 ／ 1 首屏：同一個畫面，風景釘住不動，只有導覽列與文案在變 -->
    <!-- data-stop：整屏分頁的停留點，滾輪分頁與原生吸附看的是同一組位置 -->
    <section class="hero-block" data-stop>
      <div
        class="hero-sticky"
        :class="{ 'is-on': pastIntro }"
        @mousemove="onMove"
        @mouseleave="resetMove"
      >
        <div class="scene">
          <div class="stage" :style="{ '--mx': mx, '--my': my }">
            <div class="pl l-base">
              <div class="pl-in"><img :src="baseImg" alt="" /></div>
            </div>
            <div class="pl l-mount">
              <div class="pl-in"><img :src="mountImg" alt="" /></div>
            </div>
            <div class="pl l-forest">
              <div class="pl-in"><img :src="treeImg" alt="" /></div>
            </div>
            <div class="pl l-people">
              <div class="pl-in">
                <img :src="peopleImg" alt="兩位登山者站在稜線上遠眺山谷" />
              </div>
            </div>
          </div>
          <!-- 遮罩暫時隱藏 -->
          <!-- <div class="scrim"></div> -->
        </div>

        <div class="hero-inner">
          <p class="eyebrow">
            <i></i><span>{{ eyebrowText }}</span>
          </p>

          <h1 class="title">
            <span class="line"><em>全民署</em>邀您</span>
            <span class="line">選出心中的<em>全臺親山步道</em></span>
          </h1>

          <p class="lead">{{ leadText }}</p>

          <div class="cta">
            <NuxtLink class="btn-cta" to="/trails">
              馬上投票
              <img class="btn-arrow" :src="arrowIcon" alt="" width="22" height="15" />
            </NuxtLink>
            <p class="period">
              <img :src="calendarIcon" alt="" width="24" height="24" />
              <span v-if="ended">網路票選已結束</span>
              <span v-else
                >網路票選 <b>{{ periodText }}</b></span
              >
            </p>
          </div>

          <ul class="pills">
            <li v-for="(text, i) in pills" :key="text" :style="{ '--i': i }">{{ text }}</li>
            <li v-if="ended" class="pill-link" :style="{ '--i': pills.length }">
              <NuxtLink to="/rankings">
                查看票選結果
                <img class="btn-arrow" :src="arrowIcon" alt="" width="18" height="12" />
              </NuxtLink>
            </li>
          </ul>
        </div>

        <!-- 進站畫面整片都是風景，不給提示的話使用者不會知道要往下捲 -->
        <button class="scroll-cue" type="button" @click="toNextPane">
          <span>向下捲動</span>
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              d="M6 9l6 6 6-6"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <!-- 提供「首屏」那個 snap 停留點（進站畫面的停留點就是區塊本身的開頭） -->
      <div class="hero-snap" data-stop></div>
    </section>

    <!-- 2 本週精選步道 -->
    <!-- <section class="pane feature" data-stop>
      <div class="wrap">
        <div class="sec-head">
          <img class="deco c-a" :src="cloud1" alt="" data-reveal />
          <img class="deco c-b" :src="cloud2" alt="" data-reveal />
          <img class="deco c-c" :src="cloud3" alt="" data-reveal />
          <img class="deco hill" :src="hillImg" alt="" />
          <h2 data-reveal>本週精選步道</h2>
          <p data-reveal style="--i: 1">四大分區輪番上陣,點選卡片查看步道詳情並直接投票</p>
        </div>

        <ul class="trails">
          <li v-for="(t, i) in trails" :key="t.id" class="tcard" data-reveal :style="{ '--i': i }">
            <NuxtLink class="tcard-link" :to="`/trails/${t.id}`">
              <span class="tcard-photo" :class="'tone-' + t.tone">
                <img v-if="t.cover" class="tcard-img" :src="t.cover" :alt="t.name" />
                <span class="tcard-zone">
                  <img :src="pinIcon" alt="" width="24" height="24" />{{ t.zone }}
                </span>
              </span>
              <span class="tcard-area">{{ t.area }}</span>
              <span class="tcard-name">{{ t.name }}</span>
              <span class="tcard-meta">
                <span class="mrow">
                  <b>級數：</b><i>{{ t.level }}</i>
                  <em></em>
                  <b>單程：</b><i>{{ t.length }}</i>
                </span>
                <span class="mrow">
                  <b>所需時間：</b><i>{{ t.duration }}</i>
                </span>
                <span class="mtags">
                  <span v-for="tag in t.tags" :key="tag">{{ tag }}</span>
                </span>
              </span>
              <span class="tcard-votes">
                <b><CountUp :value="t.votes" /></b>
                目前票數
              </span>
            </NuxtLink>
          </li>
        </ul>

        <div class="sec-more" data-reveal>
          <NuxtLink class="btn-pill" to="/trails">
            瀏覽完整步道列表
            <img class="btn-arrow" :src="arrowIcon" alt="" width="20" height="13" />
          </NuxtLink>
        </div>
      </div>
    </section> -->

    <!-- 3 更多特色活動 -->
    <!-- <section class="pane play" data-stop>
      <div class="wrap">
        <div class="play-head">
          <img class="play-cloud" :src="playCloud" alt="" />
          <img class="play-art jump" :src="playJump" alt="" />
          <h2 data-reveal>更多特色活動</h2>
          <p data-reveal style="--i: 1">不只投票！答題挑戰、主題蓋章，更多特色活動等你來完成</p>
        </div>

        <div class="play-grid">
          <article
            v-for="(c, i) in playCards"
            :key="c.key"
            class="pcard"
            :class="'is-' + c.key"
            data-reveal
            :style="{ '--i': i }"
          >
            <img class="pcard-bg" :src="c.bg" alt="" />
            <img class="pcard-art" :src="c.art" alt="" />
            <div class="pcard-inner">
              <div class="pcard-text">
                <span class="pcard-badge">{{ c.badge }}</span>
                <h3 :data-text="c.title">{{ c.title }}</h3>
                <p>{{ c.desc }}</p>
              </div>
              <NuxtLink class="pcard-btn" :to="c.to">
                {{ c.action }}
                <img
                  class="pcard-caret"
                  :src="caretIcon"
                  width="17"
                  height="12"
                  alt=""
                  aria-hidden="true"
                />
              </NuxtLink>
            </div>
          </article>
        </div>
      </div>

      <img class="play-ridge" :src="ridgeImg" alt="" />
    </section> -->

    <!-- 4 頁尾：稜線已由 .play-ridge 畫在內容底下，頁尾自己那條會疊在卡片上，關掉 -->
    <div class="pane-foot" data-stop>
      <SiteFooter :ridge="false" />
    </div>
  </div>
</template>

<style scoped>
/* 整頁自己就是捲動容器，才能做整屏分頁；layout: false 所以它直接佔滿視窗 */
.scroller {
  /* 這頁專屬的色票集中在這裡，改色只改這一段；
     品牌橘與文字灰階一律沿用 main.css 的 --site-*，不再另外寫死色號 */
  /* 步道卡片 */
  --card-line: #fceedd;
  --card-face: linear-gradient(270.41deg, #fffbf4 -36.62%, #ffffff 129.29%);
  --card-zone-ink: #1e1e1e;
  --card-label: #02050a;
  --card-value: #808284;
  --card-tag-bg: #ffe696;
  --card-tag-ink: #724511;
  --card-votes-ink: #606367;
  /* 首屏：膠囊字色，以及手機版風景帶上方接續的天空 */
  --hero-pill-ink: #2c653a;
  --hero-sky: linear-gradient(180deg, #2f9ded 0%, #45abf4 58%, #4baff6 100%);
  /* 互動玩法卡片（兩張卡各自的主題色定在 .is-quiz / .is-stamp） */
  --play-badge-bg: #fdfd3a;
  --play-badge-ink: #0047ba;
  --play-desc-ink: #023d7f;
  /* 遠山剪影高度：.play 的 padding-bottom 要留同一個值，剪影才不會壓到卡片 */
  --ridge-h: 175px;
  /* 「更多互動玩法」標題上方留給雲層與插圖的高度 */
  --head-pad: 230px;

  height: 100svh;
  overflow-y: auto;
  overflow-x: hidden;
  /* 整屏分頁保留原生吸附（觸控、鍵盤、拖捲軸都靠它），
     但滾輪改由 JS 一次推一屏（見 onWheel），兩邊才不會互相拉扯 */
  scroll-snap-type: y mandatory;
  overscroll-behavior-y: contain;
  font-family: var(--site-font);
  color: var(--site-ink);
  background: #fff;
}

.pane {
  position: relative;
  min-height: 100svh;
  scroll-snap-align: start;
}

.pane-foot {
  scroll-snap-align: end;
}

.wrap {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: var(--site-max);
  margin: 0 auto;
  padding: 0 20px;
}

/* 導覽列固定在最上層，進站畫面時收起來 */
.head-fix {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  opacity: 0;
  transform: translateY(-100%);
  pointer-events: none;
  transition:
    opacity 0.45s cubic-bezier(0.22, 0.61, 0.36, 1),
    transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.head-fix.is-on {
  opacity: 1;
  transform: none;
  pointer-events: auto;
}

/* 捲動進場：影片裡各區塊是捲到才依序浮上來，不是一載入就全部播完 */
[data-reveal] {
  opacity: 0;
  transform: translateY(34px);
  transition:
    opacity 0.85s cubic-bezier(0.16, 0.84, 0.32, 1),
    transform 0.85s cubic-bezier(0.16, 0.84, 0.32, 1);
  transition-delay: calc(var(--i, 0) * 0.12s);
}

[data-reveal].is-in {
  opacity: 1;
  transform: none;
}

/* ---------------- 風景畫面（進站畫面 + 首屏） ---------------- */
/* sticky（一個畫面高）+ 一個等高的空區塊 = 兩個畫面高，
   風景因此會在前一個畫面高的捲動範圍內被釘住不動，捲完才隨頁面離開。
   isolation 把 z-index 關在區塊裡，避免釘住的風景蓋到下一個區塊 */
.hero-block {
  position: relative;
  isolation: isolate;
  scroll-snap-align: start;
}

.hero-sticky {
  --hero-h: 100svh;
  position: sticky;
  top: 0;
  z-index: 1;
  height: 100svh;
  overflow: hidden;
}

.hero-snap {
  height: 100svh;
  scroll-snap-align: start;
}

.scene {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

/* 四層素材同為 1440×810 的原稿座標系（底圖 1440×810、山 1440×501、
   樹 1440×494、人物 608×398），stage 先等比放大到足以覆蓋畫面，
   層與層的相對位置才不會跑掉 */
.stage {
  /* 滑鼠視差會把每一層推離畫面邊緣，stage 先四邊各留一圈出血，
     推到底也不會在邊緣露出底色白線 */
  --bleed: 1.2vw;
  /* 進站→首屏的推鏡分兩段：整塊畫布先一起放大（這一行），
     接著山、樹、人物再依序各自往前長一點（見下面的 .is-on .l-* img）。
     四層是同一張畫拆出來的，底圖本身就含了山與樹，所以近景只准往前長、
     不准退開或下沉——往前長只是蓋住後面那層，退開就會露出底圖那條
     一模一樣的稜線，變成雙重邊 */
  --push: 1;
  /* 推鏡的倍率與錨點是照設計稿的兩張效果圖回推的（首頁效果圖0／1）：
     背景約放大 1.1 倍，鏡頭中心落在人物那一側偏上（77% 12%）。
     錨點壓在上方是關鍵——放大時前景的草原往下擠出畫面、天際線幾乎不動，
     那正是效果圖1 的樣子；錨在底邊會變成天空被吃掉，草原原地不動 */
  position: absolute;
  /* 畫布是 16:9，比它瘦的視窗會讓畫布寬過畫面、比它胖的會高過畫面。
     兩種溢出都不能切到右下角的登山者：橫向一律貼右（多出來的切在左邊那片草原），
     縱向一律貼底（多出來的切在上方的天空），兩處少一截都不傷構圖 */
  right: calc(var(--bleed) * -1);
  bottom: calc(var(--bleed) * -0.6);
  transform: scale(var(--push));
  transform-origin: 77% 12%;
  transition: transform 1.8s cubic-bezier(0.16, 0.84, 0.32, 1);
  width: max(
    calc(100% + var(--bleed) * 2),
    calc((var(--hero-h) + var(--bleed) * 1.2) * 1440 / 810)
  );
  aspect-ratio: 1440 / 810;
}

.is-on .stage {
  --push: 1.1;
}

/* 外層吃滑鼠視差，內層吃進場動畫，img 只負責自己的定位與縮放，
   三種 transform 才不會互相覆蓋 */
.pl {
  position: absolute;
  inset: 0;
  /* 位移量用 vw 而非 px，才能跟 stage 的出血維持同一個比例。
     深度差刻意壓小（最遠 3、最近 16，橫向 ±0.18vw～±0.96vw）：
     底圖已經含了山與樹，層與層錯開太多就會在稜線上看到重影 */
  transform: translate3d(
    calc(var(--mx, 0) * var(--d) * -0.06vw),
    calc(var(--my, 0) * var(--d) * -0.033vw),
    0
  );
  transition: transform 0.6s cubic-bezier(0.22, 0.61, 0.36, 1);
  will-change: transform;
}

.pl-in {
  position: absolute;
  inset: 0;
  animation: layerIn 1.1s ease-out both;
}

.pl img {
  position: absolute;
  transition: transform 1.6s cubic-bezier(0.16, 0.84, 0.32, 1);
  transition-delay: var(--zoom-delay, 0s);
}

/* 底圖是完整的一張風景，另外三層都是從它身上拆出來的近景 */
.l-base {
  --d: 3;
}

.l-base img {
  inset: 0;
  width: 100%;
  height: 100%;
}

.l-mount {
  --d: 6;
}

/* 山原稿 1440×501，對齊畫布底部。
   放大的錨點壓在底邊（下緣貼齊畫布，長高不會在底部露出底圖），
   橫向錨在人物那一側，鏡頭才像是往登山者的方向靠 */
.l-mount img {
  left: 0;
  bottom: 0;
  width: 100%;
  height: 61.8519%;
  transform-origin: 76% 100%;
  transform: scale(1);
}

/* 依序的第一拍：山先動 */
.is-on .l-mount img {
  transform: scale(1.03);
}

.l-forest {
  --d: 10;
}

/* 樹原稿 1440×494。它在設計稿裡比畫布底部再低 45px（5.5556%），
   下緣因此掛在畫面外，視差往上推也不會露出下面那層 */
.l-forest img {
  left: 0;
  bottom: -5.5556%;
  width: 100%;
  height: 60.9877%;
  transform-origin: 76% 100%;
  transform: scale(1);
  --zoom-delay: 0.22s;
}

/* 第二拍：山長完換樹，比山多長一點，近景才推得比遠景快 */
.is-on .l-forest img {
  transform: scale(1.06);
}

.l-people {
  --d: 16;
}

/* 人物是去背的切圖，裁切框跟著設計端重新輸出會變（曾經是 608×398，現在是 1824×1096），
   所以位置用「相對畫布的四邊距 + 寬度」描述、高度交給 aspect 自己算，
   換圖只要重對一次矩形，不會因為寫死高度而被拉扁。
   下面這組是拿效果圖0 回推的：在 1440×810 的原稿裡佔 (832, 412) 起、587×353 大。
   進站畫面就是效果圖0 的原尺寸（1.0），不另外縮小 */
.l-people img {
  right: 1.4356%;
  bottom: 5.5664%;
  width: 40.7867%;
  height: auto;
  /* 放大的錨點是畫布的右下角，換算成這張切圖自己的座標就是這兩個數
     （超過 100% 是因為切圖的右下緣還沒碰到畫布角落） */
  transform-origin: 103.52% 112.78%;
  /* 切圖的右緣與底緣是硬生生裁掉的（右下那片草坡與小徑在圖框上被切斷），
     只要那兩條邊落在畫面內，就會在右下角看到一個直角的接縫框。
     首屏靠推鏡把它擠出畫面（整塊畫布與人物各自往前長，右下角就被推到畫布外了），
     沒有推鏡的進站畫面就得自己推這一把——位移量用切圖自己的百分比，
     換視窗大小也維持同一個比例，滑鼠視差推到底（±0.96vw）仍留得住 */
  transform: translate(7%, 18%) scale(1);
  --zoom-delay: 0.44s;
}

/* 第三拍：人物最後動，也長得最多。照效果圖1 回推是 1.42
   （那張的登山者是效果圖0 的 1.54 倍，扣掉整塊畫布的 1.1 推鏡），
   實際看下來那個尺寸的登山者壓過了整片山谷，手動收到 1.056、比設計稿小約 26%。
   錨點在右下角，長大後岩石會壓出畫面下緣，跟效果圖1 一樣 */
.is-on .l-people img {
  transform: translate(0, 0) scale(1.056);
}

.l-base .pl-in {
  animation-delay: 0.05s;
}

.l-mount .pl-in {
  animation-delay: 0.18s;
}

.l-forest .pl-in {
  animation-delay: 0.31s;
}

.l-people .pl-in {
  animation-delay: 0.66s;
}

/* 文字壓在偏亮的天空上，補一層白霧確保可讀性（視覺稿本身也帶這層）；
   進站畫面沒有文字，就不加這層，風景維持原色 */
.scrim {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 1.2s ease-out;
  background: linear-gradient(
    100deg,
    rgba(255, 255, 255, 0.62) 0%,
    rgba(255, 255, 255, 0.34) 34%,
    rgba(255, 255, 255, 0.06) 56%,
    rgba(255, 255, 255, 0) 70%
  );
}

.is-on .scrim {
  opacity: 1;
}

.scroll-cue {
  position: absolute;
  z-index: 2;
  left: 50%;
  bottom: 40px;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 18px;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.5);
  color: var(--site-ink);
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 2px;
  cursor: pointer;
  /* backwards 而不是 both：動畫結束後要交還給 .is-on 的規則，否則蓋不掉 */
  animation: cueIn 1s cubic-bezier(0.16, 0.84, 0.32, 1) 1.6s backwards;
  transition:
    background-color 0.25s,
    opacity 0.45s,
    transform 0.45s;
}

.is-on .scroll-cue {
  opacity: 0;
  transform: translate(-50%, 14px);
  pointer-events: none;
}

.scroll-cue:hover {
  background: rgba(255, 255, 255, 0.78);
}

.scroll-cue svg {
  animation: cueBounce 1.8s ease-in-out infinite;
}

/* ---------------- 首屏文案 ---------------- */
.hero-inner {
  position: absolute;
  z-index: 2;
  left: 50%;
  top: var(--site-header-h);
  bottom: 0;
  width: 100%;
  max-width: var(--site-max);
  transform: translateX(-50%);
  /* 底部留白讓置中的重心往上偏，視覺上比純上下居中穩 */
  padding: 0 20px 9vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  pointer-events: none;
}

.hero-inner a,
.hero-inner li {
  pointer-events: auto;
}

.eyebrow {
  display: flex;
  align-items: center;
  gap: 20px;
  margin: 0 0 40px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.7;
  letter-spacing: 3px;
  color: var(--site-orange);
}

.eyebrow i {
  display: block;
  width: 100px;
  height: 1px;
  background: var(--site-orange);
  transform-origin: left;
  transform: scaleX(0);
  transition: transform 0.9s cubic-bezier(0.16, 0.84, 0.32, 1) 0.24s;
}

.is-on .eyebrow i {
  transform: scaleX(1);
}

.title {
  margin: 0 0 40px;
  font-size: clamp(34px, 5vw, 66px);
  font-weight: 600;
  line-height: 1.2;
}

.title .line {
  display: block;
}

.title em {
  font-style: normal;
  color: var(--site-orange);
}

.lead {
  max-width: 620px;
  margin: 0 0 40px;
  font-size: 18px;
  line-height: 2;
  color: #ffffff;
}

.cta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 18px 36px;
  margin-bottom: 40px;
}

.btn-cta {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  height: 80px;
  padding: 0 52px;
  border-radius: 12px;
  background: var(--site-orange);
  color: #fff;
  font-size: 26px;
  font-weight: 400;
  letter-spacing: 1px;
  box-shadow: -8px 8px 28px rgba(0, 0, 0, 0.06);
  transition:
    transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1),
    background-color 0.28s,
    box-shadow 0.28s;
}

.btn-arrow {
  display: block;
  transition: transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.btn-cta:hover {
  background: var(--site-orange-deep);
  transform: translateY(-3px);
  box-shadow: 0 18px 34px rgba(var(--site-orange-rgb), 0.4);
}

.btn-cta:hover .btn-arrow {
  transform: translateX(6px);
}

/* 字色沿用 .scroller 的 --site-ink，不另外指定 */
.period {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 0;
  font-size: 16px;
  color: #ffffff;
}

.period img {
  display: block;
  flex: none;
}

.period b {
  font-weight: 700;
  font-size: 20px;
}

.pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.pills li {
  padding: 7px 20px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 4px 14px rgba(var(--site-shadow-rgb), 0.1);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 400;
  color: var(--hero-pill-ink);
}

.pills li:hover {
  box-shadow: 0 10px 22px rgba(var(--site-shadow-rgb), 0.16);
}

/* 可點的那一顆：底色與內距讓給裡面的連結，整顆膠囊才都按得到 */
.pills li.pill-link {
  padding: 0;
  background: var(--site-orange);
}

.pills li.pill-link a {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 20px;
  color: #fff;
  transition: background-color 0.28s;
  border-radius: inherit;
}

.pills li.pill-link:hover {
  box-shadow: 0 10px 22px rgba(var(--site-orange-rgb), 0.34);
}

.pills li.pill-link a:hover {
  background: var(--site-orange-deep);
}

.pills li.pill-link a:hover .btn-arrow {
  transform: translateX(4px);
}

/* 文案在捲到首屏時才由左側緩慢進入，進站畫面只有風景 */
.eyebrow,
.title .line,
.lead,
.cta,
.pills li {
  opacity: 0;
  transform: translateX(-64px);
  transition:
    opacity 1.1s cubic-bezier(0.16, 0.84, 0.32, 1),
    transform 1.1s cubic-bezier(0.16, 0.84, 0.32, 1),
    box-shadow 0.25s;
}

.eyebrow {
  transition-delay: 0.1s;
}

.title .line:nth-child(1) {
  transition-delay: 0.24s;
}

.title .line:nth-child(2) {
  transition-delay: 0.36s;
}

.lead {
  transition-delay: 0.5s;
}

.cta {
  transition-delay: 0.62s;
}

.pills li {
  transition-delay: calc(0.74s + var(--i) * 0.1s);
}

.is-on .eyebrow,
.is-on .title .line,
.is-on .lead,
.is-on .cta,
.is-on .pills li {
  opacity: 1;
  transform: none;
}

.is-on .pills li:hover {
  transform: translateY(-3px);
}

/* ---------------- 本週精選步道 ---------------- */
.feature {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: calc(var(--site-header-h) + 14px) 0 26px;
  background: #fff;
}

.sec-head {
  position: relative;
  padding: 4px 0 30px;
  text-align: center;
}

/* 手機到平板之間字級跟著寬度收，48px 的大標在窄螢幕上會整行撐滿 */
.sec-head h2 {
  position: relative;
  z-index: 1;
  margin: 0 0 12px;
  font-size: clamp(28px, 5.2vw, 48px);
  font-weight: 600;
  color: var(--site-title);
}

.sec-head p {
  position: relative;
  z-index: 1;
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  color: var(--site-text-sub);
}

.deco {
  position: absolute;
  pointer-events: none;
  user-select: none;
}

/* 雲朵是捲到才飄進來的：雲朵1 自左往右，雲朵2、雲朵3 自右往左。
   飄進來的終點刻意等於 floatX 的起點，接手無限飄浮時才不會跳一下；
   三朵都用 alternate、只靠不同秒數錯開，否則 alternate-reverse 的起點對不上 */
.deco.c-a,
.deco.c-b,
.deco.c-c {
  opacity: 0;
}

.deco.c-a.is-in,
.deco.c-b.is-in,
.deco.c-c.is-in {
  opacity: 1;
}

.deco.c-a {
  left: 32px;
  top: 40px;
  width: 13.1%;
}

.deco.c-a.is-in {
  animation:
    cloudInL 1.1s cubic-bezier(0.22, 0.61, 0.36, 1) both,
    floatX 17s ease-in-out 1.1s infinite alternate;
}

.deco.c-b {
  left: 21%;
  top: -6px;
  width: 8%;
}

.deco.c-b.is-in {
  animation:
    cloudInR 1.2s cubic-bezier(0.22, 0.61, 0.36, 1) 0.12s both,
    floatX 23s ease-in-out 1.32s infinite alternate;
}

.deco.c-c {
  left: 69.6%;
  top: 20px;
  width: 6.6%;
}

.deco.c-c.is-in {
  animation:
    cloudInR 1.2s cubic-bezier(0.22, 0.61, 0.36, 1) 0.24s both,
    floatX 20s ease-in-out 1.44s infinite alternate;
}

.deco.hill {
  right: 0;
  top: 15px;
  width: 29.4%;
  animation: floatY 9s ease-in-out infinite alternate;
}

/* 一行四張（一區一張），1180 的內容寬剛好是 4×280 + 3×20 */
.trails {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* 步道卡片：由下往上一張接一張浮上來，起跑點排在雲朵與標題之後 */
.tcard[data-reveal] {
  transform: translateY(56px);
  transition-duration: 0.7s;
  transition-delay: calc(0.3s + var(--i, 0) * 0.18s);
}

.tcard[data-reveal].is-in {
  transform: none;
}

/* 標籤數量不一樣時卡片會長短不齊，讓資訊區把剩下的高度吃掉，四張的票數才會切齊底部 */
.tcard-link {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tcard-photo {
  position: relative;
  display: block;
  aspect-ratio: 280 / 288;
  border-radius: 20px;
  overflow: hidden;
  transition: transform 0.35s cubic-bezier(0.22, 0.61, 0.36, 1);
}

/* 封面圖蓋在漸層之上；沒設定封面時就只剩底下的漸層 */
.tcard-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 沒有封面圖時，用貼近視覺稿三張照片色調的漸層佔位 */
.tone-sunrise {
  background: linear-gradient(165deg, #f8cfa6 0%, #d99a7c 42%, #4c3f45 100%);
}

.tone-aurora {
  background: linear-gradient(165deg, #2f6f6b 0%, #86bb8c 46%, #1c3540 100%);
}

.tone-fjord {
  background: linear-gradient(165deg, #7fa27a 0%, #4b7a6c 45%, #26424a 100%);
}

.tone-moss {
  background: linear-gradient(165deg, #b9cf94 0%, #6f9a63 45%, #33513c 100%);
}

.tcard:hover .tcard-photo {
  transform: translateY(-6px);
}

.tcard-zone {
  position: absolute;
  right: 14px;
  bottom: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(var(--site-shadow-rgb), 0.18);
  font-size: 16px;
  font-weight: 500;
  color: var(--card-zone-ink);
}

.tcard-area {
  display: block;
  margin: 16px 0 8px;
  font-size: 13px;
  line-height: 1.4;
  color: var(--site-text-weak);
}

.tcard-name {
  display: block;
  margin-bottom: 16px;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.2;
  transition: color 0.25s;
}

.tcard:hover .tcard-name {
  color: var(--site-orange);
}

.tcard-meta {
  display: block;
  flex: 1;
  padding: 14px;
  border: 1px solid var(--card-line);
  border-radius: 14px;
  background: var(--card-face);
}

.mrow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  font-size: 13px;
  line-height: 1.6;
  font-weight: 500;
  color: var(--card-label);
}

/* 卡片變窄後三個欄位擠不進同一行，改成「難度／里程」一行、「所需時間」一行 */
.mrow + .mrow {
  margin-top: 6px;
}

.mrow i {
  font-style: normal;
  color: var(--card-value);
}

.mrow em {
  width: 1px;
  height: 19px;
  margin: 0 5px;
  background: var(--site-line);
}

.mtags {
  --mtag-h: 34px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
  /* 卡片只留一行標籤，排不下的整顆被裁掉，要看全部請進步道詳情。
     用 wrap 加單行高度而不是 nowrap，換行的標籤才會整顆消失、不會切一半 */
  max-height: var(--mtag-h);
  overflow: hidden;
}

.mtags span {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  height: var(--mtag-h);
  padding: 0 16px;
  border-radius: 999px;
  background: var(--card-tag-bg);
  font-size: 14px;
  line-height: 1;
  color: var(--card-tag-ink);
  white-space: nowrap;
}

.tcard-votes {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 16px;
  font-size: 13px;
  color: var(--card-votes-ink);
}

.tcard-votes b {
  font-size: 26px;
  font-weight: 700;
  color: var(--site-orange-bright);
}

.sec-more {
  margin-top: 30px;
  text-align: center;
}

.btn-pill {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 45px;
  padding: 0 20px;
  border-radius: 14px;
  background: var(--site-orange-soft);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  box-shadow: 0 6px 12px rgba(var(--site-orange-soft-rgb), 0.2);
  transition:
    transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1),
    background-color 0.28s,
    box-shadow 0.28s;
}

.btn-pill:hover {
  background: var(--site-orange-deep);
  transform: translateY(-3px);
  box-shadow: 0 14px 28px rgba(var(--site-orange-rgb), 0.36);
}

.btn-pill:hover .btn-arrow {
  transform: translateX(6px);
}

/* ---------------- 更多互動玩法 ---------------- */
.play {
  position: relative;
  /* 雲層與遠山都靠負 z-index 壓在內容底下，把層級關在這一區才不會影響別區 */
  isolation: isolate;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: calc(var(--site-header-h) + 10px) 0 var(--ridge-h);
  background: #fff;
  overflow: hidden;
}

/* .wrap 預設的 z-index 會把雲層關在自己的層級裡，壓住底下的遠山，這裡拆掉；
   內容本來就在負層之上，不靠這個 z-index */
.play > .wrap {
  z-index: auto;
}

/* 遠山剪影本身就是接近白的淺灰，貼齊底邊即可；太窄時等比撐開才不會被壓扁。
   排在雲層之後，才不會被雲層底部的純白蓋掉 */
.play-ridge {
  position: absolute;
  z-index: -1;
  left: 50%;
  bottom: 0;
  width: 100%;
  min-width: 1440px;
  /* 高度鎖成 --ridge-h（= .play 預留的 padding-bottom），寬螢幕上才不會等比長高
     而往上吃掉卡片的空間；多出來的寬度讓剪影橫向拉開即可 */
  height: var(--ridge-h);
  transform: translateX(-50%);
  pointer-events: none;
  user-select: none;
}

.play-head {
  position: relative;
  padding: var(--head-pad) 0 34px;
}

/* 雲層素材是 #f7f7f7 漸層到白，直接壓在白底上就是視覺稿的效果。
   雲要停在標題上方固定的比例位置，所以位置跟著 --head-pad 走，不各自寫死 */
.play-cloud {
  position: absolute;
  z-index: -1;
  left: 50%;
  top: calc(var(--head-pad) * 0.66);
  width: 100vw;
  transform: translateX(-50%);
  pointer-events: none;
}

.play-head h2 {
  margin: 0 0 12px;
  font-size: clamp(24px, 3.4vw, 32px);
  font-weight: 700;
  color: var(--site-title);
}

.play-head p {
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  color: var(--site-text-sub);
}

.play-art {
  position: absolute;
  pointer-events: none;
  user-select: none;
}

.play-art.book {
  left: -14px;
  top: 8px;
  width: 237px;
  animation: floatY 7s ease-in-out infinite alternate;
}

.play-art.jump {
  right: -16px;
  bottom: 36px;
  width: 425px;
  animation: floatY 3.5s ease-in-out infinite alternate-reverse;
}

.play-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 41px;
}

/* 進場（左右對開）走 translate、hover 抬起走 transform，
   兩個屬性各自獨立才不會互相蓋掉，也能給不同的時間曲線 */
.pcard {
  position: relative;
  transition:
    opacity 0.9s cubic-bezier(0.16, 0.84, 0.32, 1),
    translate 0.9s cubic-bezier(0.16, 0.84, 0.32, 1),
    transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}

/* 蓋掉共用的「由下浮上」，改成問答由左、蓋章由右；
   要有對開的感覺就得同時進場，所以錯開時間歸零 */
.pcard[data-reveal] {
  transform: none;
  transition-delay: 0s;
}

.pcard.is-quiz[data-reveal] {
  translate: -72px 0;
}

.pcard.is-stamp[data-reveal] {
  translate: 72px 0;
}

.pcard[data-reveal].is-in {
  translate: none;
}

.pcard:hover {
  transform: translateY(-5px);
}

/* 兩張卡的差別只有一組主題色，抽成變數後底下的規則就能共用一份 */
.pcard.is-quiz {
  --pc-edge: #329500;
  --pc-core: #082400;
  --pc-btn: linear-gradient(135deg, #35b3ff, #0f8ff5);
  --pc-btn-shadow: rgba(16, 130, 224, 0.32);
}

.pcard.is-stamp {
  --pc-edge: #329500;
  --pc-core: #082400;
  --pc-btn: linear-gradient(135deg, #9b5cff, #7434e8);
  --pc-btn-shadow: rgba(116, 52, 232, 0.32);
}

/* 底圖已經把缺角造型、圓角與菱格紋理畫好了，拉滿卡片即可 */
.pcard-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.pcard-art {
  position: absolute;
  z-index: 1;
  right: 11px;
  top: -14px;
  width: 150px;
  pointer-events: none;
}

.pcard-inner {
  position: relative;
  z-index: 2;
  min-height: 242px;
  padding: 30px 20px;
  display: flex;
  /* 設計稿是文字置中、按鈕貼底：兩張卡的說明行數不同（2 行／3 行），文字置中才會
     上下留白一致；按鈕若也置中會撞上右上角的插圖，所以另外 align-self 貼底 */
  align-items: center;
  gap: 38px;
}

.pcard-text {
  flex: 1 1 auto;
  min-width: 0;
}

.pcard-badge {
  display: inline-block;
  margin-bottom: 14px;
  padding: 6px 10px;
  border-radius: 999px;
  background: var(--play-badge-bg);
  font-size: 13px;
  font-weight: 400;
  color: var(--play-badge-ink);
}

/* 白邊在下、漸層在上：text-stroke 會往字內吃，拆兩層才留得住完整的 1px 外框 */
.pcard-text h3 {
  position: relative;
  margin: 0 0 14px;
  font-size: 26px;
  font-weight: 900;
  line-height: 1.2;
  color: transparent;
  -webkit-text-stroke: 1px #ffffff;
}

.pcard-text h3::before {
  content: attr(data-text);
  position: absolute;
  inset: 0;
  -webkit-text-stroke: 0;
  background-image: linear-gradient(
    90deg,
    var(--pc-edge) 0%,
    var(--pc-core) 20%,
    var(--pc-core) 80%,
    var(--pc-edge) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.pcard-text p {
  margin: 0;
  font-size: 16px;
  line-height: 1.2;
  color: var(--play-desc-ink);
}

.pcard-btn {
  flex: none;
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  height: 65px;
  padding: 0 20px;
  border-radius: 999px;
  background: #ffffff;
  /* box-shadow: 0 10px 22px var(--pc-btn-shadow); */
  color: #000000;
  font-size: 20px;
  font-weight: 700;
  transition:
    transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1),
    box-shadow 0.25s;
}

.pcard-caret {
  display: block;
  flex: none;
  width: 9px;
  height: 13px;
}

.pcard-btn:hover {
  transform: translateY(-2px) scale(1.03);
}

/* ---------------- 動畫 ---------------- */
/* 進站時四層只是淡入，不做任何縮放 */
@keyframes layerIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes cueIn {
  from {
    opacity: 0;
    transform: translate(-50%, 20px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@keyframes cueBounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(5px);
  }
}

@keyframes cloudInL {
  from {
    opacity: 0;
    transform: translate3d(-22vw, 14px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(-14px, 0, 0);
  }
}
@keyframes cloudInR {
  from {
    opacity: 0;
    transform: translate3d(22vw, 14px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(-14px, 0, 0);
  }
}
@keyframes floatX {
  from {
    transform: translate3d(-14px, 0, 0);
  }
  to {
    transform: translate3d(16px, -6px, 0);
  }
}

@keyframes floatY {
  from {
    transform: translate3d(0, -7px, 0);
  }
  to {
    transform: translate3d(0, 9px, 0);
  }
}

/* 桌機是整屏分頁，「本週精選步道」的標題、卡片與底下的按鈕必須同時待在一個畫面裡。
   固定間距在某些視窗高度會剛好把按鈕擠到下一屏，所以整段垂直節奏都跟著 svh 連續縮放 */
@media (min-width: 901px) {
  .feature {
    padding: calc(var(--site-header-h) + clamp(6px, 1.4svh, 14px)) 0 clamp(16px, 2.6svh, 26px);
  }

  .sec-head {
    padding: 0 0 clamp(20px, 3svh, 30px);
  }

  .sec-head h2 {
    margin-bottom: clamp(8px, 1.2svh, 12px);
    font-size: clamp(30px, 4.6svh, 48px);
  }

  .trails {
    gap: clamp(14px, 1.6vw, 20px);
  }

  /* 桌機才需要壓照片高度：整段要塞進一個畫面。手機是一般捲動，照片維持原比例。
     這裡只能給 height 不能給 max-height：aspect-ratio 會把高度上限反推回寬度，
     照片就比底下的資訊區窄了一截 */
  .tcard-photo {
    aspect-ratio: auto;
    height: clamp(160px, 26svh, 288px);
  }

  .tcard-area {
    margin: clamp(10px, 1.5svh, 16px) 0 clamp(4px, 1svh, 8px);
  }

  .tcard-name {
    margin-bottom: clamp(8px, 1.6svh, 16px);
    font-size: clamp(18px, 2.3svh, 22px);
  }

  .tcard-meta {
    padding: clamp(10px, 1.4svh, 14px) 14px;
  }

  .mtags {
    --mtag-h: clamp(26px, 3.5svh, 34px);
    margin-top: clamp(8px, 1.2svh, 12px);
  }

  .mtags span {
    padding: 0 clamp(10px, 1.2vw, 16px);
    font-size: clamp(12px, 1.4svh, 14px);
  }

  .tcard-votes {
    margin-top: clamp(10px, 1.6svh, 16px);
  }

  .tcard-votes b {
    font-size: clamp(21px, 2.7svh, 26px);
  }

  /* 卡片與按鈕貼太近時整段會頭重腳輕，間距放大一階；
     四欄的卡片比三欄時高，上限跟著收一階，按鈕才不會被擠出這一屏 */
  .sec-more {
    margin-top: clamp(22px, 3.4svh, 44px);
  }

  /* 「更多互動玩法」同樣是一屏一段，用同一套做法。
     之前是 max-height:920px 一刀切，落在 901～1080 寬時又會被下面的平板斷點蓋回去，
     1024×768 的筆電就會把卡片與稜線頂出這一屏 */
  .play {
    --head-pad: clamp(150px, 23svh, 230px);
    --ridge-h: clamp(115px, 18svh, 175px);
    padding-top: calc(var(--site-header-h) + clamp(6px, 1.2svh, 10px));
  }

  .play-head {
    padding-bottom: clamp(22px, 3.4svh, 34px);
  }

  .play-art.book {
    width: clamp(180px, 26svh, 237px);
  }

  .play-art.jump {
    width: clamp(206px, 29svh, 425px);
  }

  .pcard-inner {
    min-height: clamp(190px, 26svh, 242px);
    padding: clamp(18px, 2.6svh, 30px) 20px;
  }

  .pcard-btn {
    height: clamp(46px, 7svh, 65px);
    font-size: clamp(16px, 2.2svh, 20px);
  }
}

/* 1200 以下四欄更窄，標籤會一行一顆、卡片跟著長高，
   768 高的筆電上剛好把按鈕頂出這一屏；照片上限再壓一階把高度還回來 */
@media (min-width: 901px) and (max-width: 1200px) {
  .tcard-photo {
    height: clamp(140px, 20svh, 240px);
  }

  .mtags {
    --mtag-h: clamp(26px, 3.5svh, 32px);
    gap: 8px;
  }

  .mtags span {
    padding: 0 12px;
  }
}

/* ---------------- 平板 ---------------- */
@media (max-width: 1080px) {
  .trails {
    gap: 14px;
  }

  /* 這個寬度只剩一半版面，標題與按鈕照桌機尺寸會把文案擠成細長一條。
     高度相關的尺寸交給上面的 svh 規則，這裡只調跟寬度有關的 */
  .pcard-inner {
    gap: 22px;
  }

  .pcard-text h3 {
    font-size: 26px;
    line-height: 1.15;
  }

  .pcard-text p {
    font-size: 16px;
    line-height: 1.5;
  }

  .pcard-btn {
    padding: 0 16px;
    font-size: 17px;
  }
}

/* ---------------- 手機 ---------------- */
/* 直式畫面塞不下整屏分頁（內容常常比一個畫面高），改回一般捲動；
   首屏若照桌機那樣滿版裁切也只會剩下登山者的下半身，
   所以改成「下半部風景帶 + 上方接續天空漸層」，整組構圖才留得住 */
@media (max-width: 900px) {
  .scroller {
    scroll-snap-type: none;
  }

  .pane {
    min-height: 0;
  }

  /* 直式畫面塞不下橫幅構圖，改成「下半部風景帶 + 上方接續天空漸層」，
     風景帶高度由剩餘空間決定，文字就不會壓到登山者 */
  .hero-sticky {
    --band-h: max(340px, calc(100svh - 374px));
  }

  .scene {
    background: var(--hero-sky);
  }

  .stage {
    /* 風景帶本來就貼齊畫面底部，直式也不做推鏡 */
    --push: 1;
    left: auto;
    right: 0;
    bottom: 0;
    transform: none;
    width: max(100%, calc(var(--band-h) * 1440 / 810));
    -webkit-mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 15%);
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 15%);
  }

  /* 直式只露出下半部構圖，人物照桌機長到 1.42 會被上緣的漸層切掉頭，
     收斂到剛好站滿風景帶 */
  .is-on .l-people img {
    /* 直式的畫布不推鏡（--push 固定 1），1.15 推不出裁切邊，
       這一態也要自己帶位移，理由同桌機的進站畫面 */
    transform: translate(7%, 19%) scale(1.15);
  }

  .scrim {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.84) 0%,
      rgba(255, 255, 255, 0.7) 40%,
      rgba(255, 255, 255, 0.3) 62%,
      rgba(255, 255, 255, 0) 80%
    );
  }

  /* hero-inner 的 top 已經是導覽列高度，這裡只補間距，別再重複加一次 */
  .hero-inner {
    justify-content: flex-start;
    padding: 28px 20px 0;
  }

  .eyebrow,
  .title .line,
  .lead,
  .cta,
  .pills li {
    transform: translateX(-36px);
  }

  .eyebrow {
    gap: 12px;
    margin-bottom: 18px;
    font-size: 13px;
    letter-spacing: 1.5px;
  }

  .eyebrow i {
    width: 34px;
  }

  .title {
    margin-bottom: 20px;
    line-height: 1.34;
    letter-spacing: 1px;
  }

  /* 窄螢幕一行字數少、折行多，正文行距收一點才不會整段散開 */
  .lead {
    margin-bottom: 26px;
    line-height: 1.8;
  }

  .cta {
    gap: 14px 20px;
    margin-bottom: 26px;
  }

  .btn-cta {
    height: 54px;
    padding: 0 26px;
    font-size: 16px;
  }

  .period {
    font-size: 15px;
    gap: 9px;
  }

  .pills {
    gap: 8px;
  }

  .pills li {
    padding: 9px 16px;
    font-size: 13px;
  }

  .pills li.pill-link a {
    padding: 9px 16px;
  }

  .feature {
    padding: 64px 0 72px;
  }

  .sec-head {
    padding: 8px 0 34px;
  }

  /* 兩行以上的說明文在手機上要比桌機再鬆一點才好讀 */
  .sec-head p,
  .play-head p {
    line-height: 1.6;
  }

  .deco.c-a,
  .deco.c-b,
  .deco.c-c {
    display: none;
  }

  .deco.hill {
    position: static;
    display: block;
    width: 190px;
    margin: 0 auto 14px;
  }

  /* 平板放得下兩欄就排兩欄，手機才落到單欄 */
  .trails {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 34px 24px;
    max-width: 420px;
    margin: 0 auto;
  }

  /* 剪影跟著窄螢幕縮短，padding-bottom 也就跟著收，卡片與剪影才不會互相壓到 */
  .play {
    --head-pad: 190px;
    --ridge-h: 110px;
    padding-top: 30px;
  }

  /* 最小寬度跟著高度等比縮，否則手機上只看得到剪影中間一小段、山形也被壓扁 */
  .play-ridge {
    min-width: 900px;
  }

  .play-head {
    padding-bottom: 30px;
  }

  .play-art.book {
    left: -14px;
    width: 140px;
  }

  .play-art.jump {
    right: -6px;
    width: 165px;
  }

  /* 插圖在直式畫面會壓到右半邊，說明文限制在左半邊才不會被人物蓋住 */
  .play-head p {
    max-width: 56%;
  }

  .play-grid {
    grid-template-columns: 1fr;
    gap: 76px;
  }

  .pcard-art {
    right: 4px;
    top: -24px;
    width: 118px;
  }

  .pcard-inner {
    min-height: 0;
    padding: 24px 22px 26px 24px;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }

  /* 卡片標題在手機上會折到三行，行距要比桌機的緊排再放開一點 */
  .pcard-text h3 {
    line-height: 1.25;
  }

  /* 直式單欄時按鈕自成一列，桌機的 65px 在手機上顯得笨重 */
  .pcard-btn {
    align-self: flex-start;
    height: 52px;
  }
}

@media (min-width: 640px) and (max-width: 900px) {
  .trails {
    max-width: none;
  }
}

@media (max-width: 420px) {
  .title {
    font-size: 30px;
    letter-spacing: 0;
  }

  .lead {
    font-size: 13.5px;
  }
}

/* 螢幕太矮時整屏分頁會把區塊內容卡住，退回一般捲動 */
@media (max-height: 740px) {
  .scroller {
    scroll-snap-type: none;
  }
}

/* ---------------- 降低動態偏好 ---------------- */
@media (prefers-reduced-motion: reduce) {
  .scroller {
    scroll-snap-type: none;
  }

  .pl-in,
  .deco,
  .play-art,
  .scroll-cue,
  .scroll-cue svg {
    animation: none !important;
  }

  /* 上面把動畫關掉後，雲朵的可見度只剩下面這行撐著 */
  .deco.c-a,
  .deco.c-b,
  .deco.c-c {
    opacity: 1;
  }

  .stage,
  .pl,
  .pl img,
  .scrim,
  .eyebrow,
  .eyebrow i,
  .title .line,
  .lead,
  .cta,
  .pills li,
  [data-reveal] {
    transition: none;
  }
}
</style>
